import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building, Plus, Pencil, Trash2, Eye, EyeOff,
  ChevronUp, ChevronDown, Save, Upload, ExternalLink, Image as ImageIcon
} from 'lucide-react';

const API = '/api';

export const AdminCMSClientsPage = () => {
  const { language } = useLanguage();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    logo_url: '',
    website_url: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/cms/clients`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name || '',
        name_ar: client.name_ar || '',
        logo_url: client.logo_url || '',
        website_url: client.website_url || '',
        description: client.description || '',
        is_active: client.is_active !== false,
      });
    } else {
      setEditingClient(null);
      setFormData({
        name: '',
        name_ar: '',
        logo_url: '',
        website_url: '',
        description: '',
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await fetch(`${API}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: uploadFormData
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, logo_url: data.url }));
        toast.success('Logo uploaded');
      } else {
        toast.error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Client name is required');
      return;
    }

    try {
      const url = editingClient 
        ? `${API}/admin/cms/clients/${editingClient.client_id}`
        : `${API}/admin/cms/clients`;
      
      const response = await fetch(url, {
        method: editingClient ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingClient ? 'Client updated' : 'Client added');
        setDialogOpen(false);
        fetchClients();
      } else {
        toast.error('Failed to save client');
      }
    } catch (error) {
      console.error('Error saving client:', error);
      toast.error('Failed to save client');
    }
  };

  const handleDelete = async () => {
    if (!clientToDelete) return;

    try {
      const response = await fetch(`${API}/admin/cms/clients/${clientToDelete.client_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        toast.success('Client deleted');
        setDeleteDialogOpen(false);
        setClientToDelete(null);
        fetchClients();
      } else {
        toast.error('Failed to delete client');
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error('Failed to delete client');
    }
  };

  const handleToggleActive = async (client) => {
    try {
      const response = await fetch(`${API}/admin/cms/clients/${client.client_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ is_active: !client.is_active })
      });

      if (response.ok) {
        toast.success(client.is_active ? 'Client hidden' : 'Client visible');
        fetchClients();
      }
    } catch (error) {
      console.error('Error toggling client:', error);
    }
  };

  const handleMoveClient = async (client, direction) => {
    const currentIndex = clients.findIndex(c => c.client_id === client.client_id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= clients.length) return;

    const newClients = [...clients];
    [newClients[currentIndex], newClients[newIndex]] = [newClients[newIndex], newClients[currentIndex]];
    
    const orders = newClients.map((c, i) => ({ client_id: c.client_id, order: i + 1 }));

    try {
      const response = await fetch(`${API}/admin/cms/clients/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(orders)
      });

      if (response.ok) {
        fetchClients();
      }
    } catch (error) {
      console.error('Error reordering:', error);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">
            {language === 'ar' ? 'إدارة العملاء' : 'Clients Management'}
          </h1>
          <p className="text-[#6B7280] mt-1">
            {language === 'ar' 
              ? 'إدارة شعارات وأسماء العملاء المعروضة على الموقع'
              : 'Manage client logos and names displayed on the website'}
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-[#33AADD] hover:bg-[#2b8eb8]" data-testid="add-client-btn">
          <Plus className="h-4 w-4 me-2" />
          {language === 'ar' ? 'إضافة عميل' : 'Add Client'}
        </Button>
      </div>

      {/* Clients Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full text-center py-8 text-[#6B7280]">Loading...</div>
        ) : clients.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-8 text-center text-[#6B7280]">
              No clients found. Add your first client.
            </CardContent>
          </Card>
        ) : (
          clients.map((client, index) => (
            <Card 
              key={client.client_id} 
              className={`transition-all ${!client.is_active ? 'opacity-60 bg-gray-50' : ''}`}
              data-testid={`client-card-${index}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col items-center text-center">
                  {/* Logo */}
                  <div className="h-20 w-32 mb-3 flex items-center justify-center bg-gray-100 rounded-lg overflow-hidden">
                    {client.logo_url ? (
                      <img 
                        src={client.logo_url} 
                        alt={client.name} 
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <Building className="h-10 w-10 text-gray-400" />
                    )}
                  </div>

                  {/* Name */}
                  <h3 className="font-semibold text-[#111827]">{client.name}</h3>
                  {client.name_ar && (
                    <p className="text-sm text-[#6B7280]" dir="rtl">{client.name_ar}</p>
                  )}
                  
                  {/* Status Badge */}
                  {!client.is_active && (
                    <Badge variant="secondary" className="mt-2 text-xs">Hidden</Badge>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-1 mt-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveClient(client, 'up')}
                      disabled={index === 0}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveClient(client, 'down')}
                      disabled={index === clients.length - 1}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleActive(client)}
                      title={client.is_active ? 'Hide' : 'Show'}
                    >
                      {client.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDialog(client)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => {
                        setClientToDelete(client);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingClient 
                ? (language === 'ar' ? 'تعديل العميل' : 'Edit Client')
                : (language === 'ar' ? 'إضافة عميل جديد' : 'Add New Client')}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar' 
                ? 'أدخل اسم العميل وشعاره'
                : 'Enter client name and logo'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Logo Upload */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'الشعار' : 'Logo'}</Label>
              <div className="flex items-center gap-4">
                <div className="h-16 w-24 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300">
                  {formData.logo_url ? (
                    <img src={formData.logo_url} alt="Logo" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-gray-400" />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('logo-upload').click()}
                    disabled={uploading}
                  >
                    <Upload className="h-4 w-4 me-2" />
                    {uploading ? 'Uploading...' : 'Upload Logo'}
                  </Button>
                  <p className="text-xs text-[#6B7280] mt-1">Max 2MB, PNG or JPG</p>
                </div>
              </div>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'اسم العميل *' : 'Client Name *'}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Acme Corporation"
                data-testid="client-name"
              />
            </div>

            {/* Arabic Name */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'الاسم بالعربي' : 'Arabic Name'}</Label>
              <Input
                value={formData.name_ar}
                onChange={(e) => setFormData({...formData, name_ar: e.target.value})}
                placeholder="مثال: شركة أكمي"
                dir="rtl"
              />
            </div>

            {/* Website URL */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'رابط الموقع' : 'Website URL'}</Label>
              <Input
                value={formData.website_url}
                onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                placeholder="https://www.example.com"
                type="url"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'وصف (اختياري)' : 'Description (optional)'}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description..."
                rows={2}
              />
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label>{language === 'ar' ? 'مرئي على الموقع' : 'Visible on Website'}</Label>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleSave} className="bg-[#33AADD] hover:bg-[#2b8eb8]">
              <Save className="h-4 w-4 me-2" />
              {language === 'ar' ? 'حفظ' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === 'ar' ? 'حذف العميل' : 'Delete Client'}</DialogTitle>
            <DialogDescription>
              {language === 'ar' 
                ? `هل أنت متأكد من حذف "${clientToDelete?.name}"؟`
                : `Are you sure you want to delete "${clientToDelete?.name}"?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 me-2" />
              {language === 'ar' ? 'حذف' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCMSClientsPage;
